import { useMemo } from 'react'
import { LineChartWrapper } from '../../charts/LineChart'
import { WidgetFrame, type WidgetLoadState } from '../../widgets/WidgetFrame'
import { formatCount } from '../../format'
import { listIngestionRuns } from '../../../../api/ingest'
import { useBoundedList, PAGE_LIMIT } from './useBoundedList'
import { bucketRunsByDay } from './analyticsMappers'
import type { IngestionRunView } from '../../../../types/ingest'

export interface IngestionRunVolumeWidgetProps {
  /**
   * The manifest's `ingestion-run-volume` capability render-gate (design §3.1 — manifest-gate-only).
   * `null` while the manifest fetch is still in flight — renders `loading`, never a premature
   * `not_authorized` flash.
   */
  available: boolean | null
  /** A stable key that changes when the caller wants a re-fetch (mirrors `useWidgetData`'s `filterKey`). */
  refreshKey: string
}

/**
 * IB-282 View 4, AC4.2 — "Ingestion runs/day". Manifest-gate-only (design §3.1): there is no
 * `/insights/widgets/ingestion-run-volume` endpoint — this tile walks the NEW, `ingestion_run:R`-gated
 * `GET /api/v1/ingestion-runs` client-side (bounded ≤2,000 rows/10 pages, {@link useBoundedList}) and
 * buckets by UTC day. An empty window renders "No activity in this period", never a fabricated
 * zero-count day (AC4.2's own failure mode).
 */
export function IngestionRunVolumeWidget({ available, refreshKey }: IngestionRunVolumeWidgetProps) {
  const { status, rows, overflow, message, retry } = useBoundedList<IngestionRunView>(
    available === true,
    (cursor, signal) => listIngestionRuns({ cursor, limit: PAGE_LIMIT }, signal),
    refreshKey,
  )

  const series = useMemo(() => bucketRunsByDay(rows), [rows])
  const frameState: WidgetLoadState =
    available === null
      ? 'loading'
      : overflow
        ? 'empty'
        : status === 'ok' && series.length === 0
          ? 'empty'
          : status
  // The "No activity"/overflow copy applies ONLY to the empty-window case — a not_authorized/error
  // state passes its OWN real message through untouched (falling back to WidgetFrame's own default
  // denial/error copy when the hook has none), never overridden by this tile's empty-state text.
  const displayMessage = overflow
    ? 'Window too large to summarize here — narrow the date range or filters.'
    : frameState === 'empty'
      ? (message ?? 'No activity in this period.')
      : message

  return (
    <WidgetFrame
      title="Ingestion runs/day"
      icon="upload"
      state={frameState}
      message={displayMessage}
      onRetry={retry}
    >
      <LineChartWrapper
        title="Ingestion runs/day"
        data={series}
        categoryKey="day"
        categoryLabel="Day"
        series={[{ key: 'count', label: 'Runs' }]}
        valueFormatter={(v) => formatCount(v)}
      />
    </WidgetFrame>
  )
}
